// Shared data for the three homepage redesign previews.
//
// These pages live at /preview/* and are NOT in seoMeta, the sitemap or the
// prerender list. They exist so the three directions can be compared as real
// clickable pages rather than as mood boards. Once one is chosen it gets
// promoted into HomePage.js and this folder goes away.
//
// Everything here is derived from the real appsData + appMeta, so the previews
// show the actual portfolio in the actual locked palettes - no placeholder art.
import { appsData } from './appsData';
import { getAppMeta } from './appMeta';

// The homepage currently renders apps in appsData order, which is the order
// they happened to be added. ALL_APPS in HomePage.js re-orders them by hand;
// this is that same list, kept here so the previews and the live page agree.
const ORDER = [
  '1', '2', '4', '6', '8', '7', '9', '10', '11', '12', '13', '14', '15', '16',
  '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30',
  '31',
];

// A real taxonomy. The tags in appsData are presentation keywords - 44 distinct
// values, most used once - so they cannot drive a filter. Six buckets, assigned
// by hand, every app in exactly one.
export const CATEGORIES = [
  { id: 'sport', label: 'Sport & scoring' },
  { id: 'trades', label: 'Trades & site' },
  { id: 'craft', label: 'Craft & making' },
  { id: 'body', label: 'Health & body' },
  { id: 'daily', label: 'Everyday tools' },
  { id: 'play', label: 'Read, play & wonder' },
];

const CATEGORY_BY_ROUTE = {
  '/bowling': 'sport',
  '/dink': 'sport',
  '/volleyball': 'sport',
  '/volleyball-score-keeper': 'sport',
  '/snooker-scoreboard': 'sport',
  '/darts-scorer': 'sport',
  '/tennis-score-keeper': 'sport',
  '/pocket-chess-clock': 'sport',

  '/conduit-fill': 'trades',
  '/ductulator': 'trades',
  '/roof-pitch': 'trades',

  '/seating-chart': 'daily',
  '/boardfoot': 'trades',

  '/craft-pricing': 'craft',
  '/needlepoint': 'craft',
  '/quilt-planner': 'craft',
  '/trace': 'craft',

  '/pcos-tracker': 'body',
  '/thrive': 'body',
  '/sterling': 'body',
  '/barbell-calculator': 'body',

  '/tip-calculator': 'daily',
  '/lease-tracker': 'daily',
  '/lightmeter': 'daily',

  '/starquest': 'play',
  '/mochirun': 'play',
  '/haiku': 'play',
  '/drum-coach': 'play',
  '/cigar-humidor': 'play',
};

const byId = Object.fromEntries(appsData.map((a) => [a.id, a]));

/// One row per visible app, already carrying its own locked palette. `live` is
/// derived from appStoreUrl rather than from the 'Coming Soon' tag, because the
/// URL is what actually decides whether a download link can render.
export const APPS = ORDER.map((id) => byId[id])
  .filter((a) => a && a.customUrl && !a.hidden)
  .map((a) => {
    const m = getAppMeta(a);
    return {
      id: a.id,
      name: a.name,
      tagline: a.tagline,
      description: a.description,
      url: a.customUrl,
      live: Boolean(a.appStoreUrl),
      // The real store links, so the homepage can send someone straight to a
      // download instead of making them go through the product page first.
      appStoreUrl: a.appStoreUrl || null,
      playStoreUrl: a.playStoreUrl || null,
      icon: m.icon,
      monogram: m.monogram,
      iconTone: m.iconTone,
      accent: m.accent,
      accentDeep: m.accentDeep,
      accentSoft: m.accentSoft,
      index: m.index,
      category: CATEGORY_BY_ROUTE[a.customUrl] || 'daily',
    };
  });

export const LIVE_COUNT = APPS.filter((a) => a.live).length;
export const SOON_COUNT = APPS.length - LIVE_COUNT;

export const countFor = (catId) => APPS.filter((a) => a.category === catId).length;

// The four the studio would lead with. Picked for range rather than for
// recency: a trades instrument, a craft calculator, a sport engine and a game,
// so four very different palettes sit next to each other.
export const FEATURED = ['/roof-pitch', '/boardfoot', '/tennis-score-keeper', '/mochirun']
  .map((u) => APPS.find((a) => a.url === u))
  .filter(Boolean);

// The guide clusters, which today get no link at all from the homepage. These
// are the hubs, not the ~190 individual articles.
export const GUIDE_CLUSTERS = [
  { label: 'Roofing', to: '/roof-pitch/guides', note: 'Pitch, slope factor, rafters' },
  { label: 'Lumber', to: '/boardfoot/guides', note: 'Board feet, 4/4 stock, log scales' },
  { label: 'Tennis', to: '/tennis-score-keeper/guides', note: 'Deuce, tiebreaks, formats' },
  { label: 'Electrical', to: '/conduit-fill/guides', note: 'NEC fill, bending, wire size' },
  { label: 'Pickleball', to: '/dink/guides', note: 'Scoring, serving, the kitchen' },
  { label: 'Volleyball', to: '/volleyball/guides', note: 'Rotations, 5-1, overlaps' },
  { label: 'Quilting', to: '/craft-pricing/guides', note: 'Pricing craft work honestly' },
  { label: 'Darts', to: '/darts-scorer/guides', note: '501, Cricket, checkouts' },
];

// ---- picking ink for a surface painted in an app's own accent ----
//
// The accents are locked per app and they are not uniformly dark. Mochi Run's
// #EF6F9B carries white text at only 2.8:1, so any direction that fills a tile
// or a slab with an accent has to choose its ink per app rather than assume
// white. Threshold 0.18 is where white stops clearing 4.5:1.
const chan = (c) => {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

export const luminance = (hex) => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return 0.2126 * chan((n >> 16) & 255) + 0.7152 * chan((n >> 8) & 255) + 0.0722 * chan(n & 255);
};

export const onAccent = (hex) =>
  (luminance(hex) > 0.18
    ? { strong: '#14160F', body: 'rgba(20,22,15,0.82)', faint: 'rgba(20,22,15,0.64)',
        rule: 'rgba(20,22,15,0.16)', btnBg: '#14160F', btnInk: '#FFFFFF' }
    : { strong: '#FFFFFF', body: 'rgba(255,255,255,0.88)', faint: 'rgba(255,255,255,0.70)',
        rule: 'rgba(255,255,255,0.20)', btnBg: '#FFFFFF', btnInk: null });
